<script setup>
import { usePageMeta } from 'composables/page-meta';
import AppRoute from 'src/router/app-route';
import { Route } from 'src/router/routes';
import { buildPageMeta } from 'src/utils/page-meta-util';

usePageMeta(buildPageMeta({
    title: 'About Xprt Dev',
    description: 'Xprt Dev is built by 0x234 to explain Australian tech job demand and advertised pay using Xprt Jobs '
        + 'listings, with an optional career profile.',
    canonicalUrl: 'https://xprt.dev/about'
}));
</script>

<template>
    <main class="about-us-page">
        <header class="market-hero">
            <div class="container-md q-px-md q-py-xl">
                <div class="hero-copy q-py-lg">
                    <p class="font-mono text-caption text-uppercase text-hint q-mb-md">About Xprt Dev · Australian ICT market intelligence</p>
                    <h1 class="text-h3 text-sm-h2 text-md-h1 text-weight-bold q-mt-none q-mb-md">Market context before the job search.</h1>
                    <p class="hero-deck text-h6 text-weight-regular text-hint q-my-none">
                        Xprt Dev is the Australian technology-market intelligence division of Xprt Jobs. It helps technology
                        workers understand active demand, advertised pay and hiring patterns before they browse roles.
                    </p>
                </div>
            </div>
        </header>
        <section aria-labelledby="why-heading" class="market-section-rule">
            <div class="section-split container-md q-px-md q-py-xl">
                <div>
                    <p class="font-mono text-caption text-uppercase text-hint q-mb-xs">Purpose</p>
                    <h2 id="why-heading" class="text-h4 text-weight-bold q-my-none">Why this product exists</h2>
                </div>
                <div>
                    <p class="text-body1 q-mt-none">
                        Job listings contain useful signals, but raw counts can be hard to interpret. Xprt Dev turns Xprt Jobs'
                        normalised listing inventory into scoped, readable market snapshots while keeping calculation limits
                        close to the figures.
                    </p>
                    <p class="text-body1 q-mb-none">
                        It does not claim whole-market coverage or predict where the market will go. Sparse salary evidence is
                        suppressed, work-arrangement counts are labelled by their actual scope, and the methodology explains how
                        every public measure is produced.
                    </p>
                </div>
            </div>
        </section>
        <section aria-labelledby="relationship-heading" class="market-section-rule" data-test="product-boundary">
            <div class="section-split container-md q-px-md q-py-xl">
                <div>
                    <p class="font-mono text-caption text-uppercase text-hint q-mb-xs">Product boundary</p>
                    <h2 id="relationship-heading" class="text-h4 text-weight-bold q-mt-none q-mb-md">Xprt Dev and Xprt Jobs</h2>
                    <p class="text-body1 text-hint q-my-none">
                        Two products with separate jobs, joined by a hand-off when you are ready to browse live roles.
                    </p>
                </div>
                <div aria-label="Product responsibilities" role="list">
                    <div class="ledger-row q-py-md" role="listitem">
                        <span class="font-mono text-caption text-uppercase text-primary">Xprt Dev</span>
                        <span class="text-body1">
                            Owns ICT market intelligence, methodology, one personal market watch and the optional career profile.
                        </span>
                    </div>
                    <div class="ledger-row q-py-md" role="listitem">
                        <span class="font-mono text-caption text-uppercase text-primary">Xprt Jobs</span>
                        <span class="text-body1">Owns job discovery, applications, employer tools, payments and job-search pages.</span>
                    </div>
                </div>
            </div>
        </section>
        <section aria-labelledby="account-heading">
            <div class="section-split container-md q-px-md q-py-xl">
                <div>
                    <p class="font-mono text-caption text-uppercase text-hint q-mb-xs">Accounts</p>
                    <h2 id="account-heading" class="text-h4 text-weight-bold q-my-none">Accounts stay optional</h2>
                </div>
                <div>
                    <p class="text-body1 q-mt-none">
                        Public market evidence is available without an account. An account can save one classification and
                        location as My Market. A career profile is a separate, optional tool with public, logged-in and
                        restricted sharing controls.
                    </p>
                    <p class="text-body1 q-mb-lg">
                        Basic details may help prefill an application only when a role accepts applications through Xprt Jobs.
                        Xprt Dev does not own applications on external employer or recruiter sites.
                    </p>
                    <div class="row q-gutter-sm">
                        <q-btn :to="AppRoute.build(Route.MarketOverview)" color="primary" label="Open the market overview" no-caps unelevated/>
                        <q-btn :to="AppRoute.build(Route.Methodology)" color="primary" label="Read the notes" no-caps outline/>
                    </div>
                </div>
            </div>
        </section>
    </main>
</template>

<style lang="scss" scoped>
.about-us-page {
    .hero-copy {
        max-width: 880px;
    }

    .hero-deck {
        max-width: 720px;
    }
}
</style>
